// Debrief state: the end-of-run payload the simulation worker posts once, plus the replay scrubber the
// whole stage shares. Kept out of `src/state/store.ts` on purpose - the live store is the worker->React
// bridge for a running fight, while everything here is frozen history that only stage 4 reads.
//
// The payload arrives through `SimClient.onEnded`, which App.tsx registers once per SimClient instance
// (the live store registers its own handler for the end banner; handlers are a list, so both fire).

import { create } from 'zustand';
import type { Event, Snapshot } from '../sim/types';
import type { OrderLogEntry, TruthFrame } from '../sim/sim';
import type { RulesetInfo } from '../sim/worker';
import { TICKS_PER_MIN } from '../sim/types';

/** Everything the worker's `ended` message carries, verbatim - including which ruleset the run played
 * under (pitch 10a), so the debrief header can show "Ruleset: realistic" (or "... (modified)"). */
export interface EndedPayload {
  snap: Snapshot;
  events: Event[];
  truth: TruthFrame[];
  orderLog: OrderLogEntry[];
  ruleset: RulesetInfo;
}

/** Ground truth is sampled once a sim minute (see `Sim.recordTruth`), so the replay steps in minutes. */
export const REPLAY_STEP_TICKS = TICKS_PER_MIN;
/** Frames per real second while the replay is playing (plan Task 19). */
export const REPLAY_FPS = 10;

interface DebriefState {
  payload: EndedPayload | null;
  /** Where the shared replay scrubber sits, in sim ticks. */
  scrubTick: number;
  playing: boolean;

  capture: (p: EndedPayload) => void;
  reset: () => void;
  setScrubTick: (tick: number) => void;
  setPlaying: (playing: boolean) => void;
  /** Jump the replay to the truth frame that covers an event and stop playback. */
  scrubToEvent: (tick: number) => void;
}

export const useDebrief = create<DebriefState>((set, get) => ({
  payload: null,
  scrubTick: 0,
  playing: false,

  capture: (payload) => set({ payload, scrubTick: payload.snap.tick, playing: false }),
  reset: () => set({ payload: null, scrubTick: 0, playing: false }),
  setScrubTick: (scrubTick) => set({ scrubTick: Math.max(0, Math.round(scrubTick)) }),
  setPlaying: (playing) => set({ playing }),

  scrubToEvent: (tick) => {
    const end = get().payload?.snap.tick ?? tick;
    const snapped = Math.floor(Math.max(0, Math.min(tick, end)) / REPLAY_STEP_TICKS) * REPLAY_STEP_TICKS;
    set({ scrubTick: snapped, playing: false });
  },
}));
